import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Sparkles, Link, Copy, ChevronLeft, ChevronRight } from 'lucide-react';
import PrepChecklist from './PrepChecklist';

/**
 * ContextSidebar - Collapsible left sidebar for Plan phase
 * 
 * Features:
 * - Strict collapse state with content completely hidden
 * - Polished circular floating toggle button
 * - Clean icon strip when collapsed
 * - State persistence via parent component
 */
const ContextSidebar = ({
    episode,
    research = [],
    isPortalActive,
    onTogglePortal,
    portalUrl,
    isCollapsed = false,
    onToggleCollapse
}) => {
    const handleDragStart = (e, item) => {
        e.dataTransfer.setData('application/json', JSON.stringify(item));
        e.dataTransfer.setData('text/plain', item.content);
        e.dataTransfer.effectAllowed = 'copy';
    };

    return (
        <div className="h-full flex flex-col border-r-2 border-slate-100 bg-white/50 backdrop-blur-sm relative">
            {/* Polished Floating Toggle Button */}
            <motion.button
                onClick={onToggleCollapse}
                whileTap={{ scale: 0.95 }}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 
                    bg-white rounded-full shadow-lg shadow-indigo-100/50 
                    border-2 border-indigo-100
                    flex items-center justify-center 
                    hover:border-indigo-300 hover:shadow-xl hover:shadow-indigo-200/50
                    hover:scale-105 active:scale-95
                    transition-all duration-200 origin-center"
            >
                <motion.div
                    animate={{ rotate: isCollapsed ? 0 : 180 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                    <ChevronRight
                        size={16}
                        className="text-indigo-500"
                    />
                </motion.div>
            </motion.button>

            {/* Collapsed State - Icon Strip */}
            <AnimatePresence mode="wait">
                {isCollapsed && (
                    <motion.div
                        key="collapsed"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="w-16 min-w-16 max-w-16 h-full flex flex-col items-center py-6 space-y-5 overflow-hidden"
                    >
                        {/* Context Icon */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50"
                        >
                            <Sparkles size={18} className="text-white" />
                        </motion.div>

                        {/* Divider */}
                        <div className="w-6 h-0.5 bg-slate-200 rounded-full" />

                        {/* Portal Status Indicator */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className={`w-11 h-11 rounded-2xl flex items-center justify-center border-2 transition-colors
                                ${isPortalActive
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`w-3 h-3 rounded-full ${isPortalActive ? 'bg-green-500 shadow-sm shadow-green-300' : 'bg-slate-300'}`} />
                        </motion.div>

                        {/* Guest Avatar */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-11 h-11 rounded-2xl bg-slate-100 overflow-hidden border-2 border-slate-200"
                        >
                            {episode?.headshotUrl ? (
                                <img src={episode.headshotUrl} alt="Guest" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                    <User size={16} />
                                </div>
                            )}
                        </motion.div>

                        {/* Checklist Icon */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center border-2 border-slate-200"
                        >
                            <span className="text-lg">📋</span>
                        </motion.div>

                        {/* Research Icon */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center border-2 border-slate-200"
                        >
                            <span className="text-lg">🔍</span>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Expanded State - Full Content */}
            <AnimatePresence mode="wait">
                {!isCollapsed && (
                    <motion.div
                        key="expanded"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col h-full min-w-[320px] max-w-[360px]"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-slate-100/50 flex justify-between items-center">
                            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <Sparkles size={12} className="text-indigo-400" />
                                Context Engine
                            </h2>
                        </div>

                        {/* Guest Portal Toggle (Quick Access) */}
                        <div className={`px-4 py-3 border-b border-slate-100/50 transition-colors ${isPortalActive ? 'bg-indigo-50/30' : 'bg-slate-50/30'}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {/* Toggle Switch */}
                                    <button
                                        onClick={() => onTogglePortal(!isPortalActive)}
                                        className={`w-8 h-4 rounded-full transition-colors relative ${isPortalActive ? 'bg-green-500' : 'bg-slate-300'}`}
                                    >
                                        <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform ${isPortalActive ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                    </button>
                                    <span className={`text-xs font-bold ${isPortalActive ? 'text-indigo-900' : 'text-slate-500'}`}>
                                        {isPortalActive ? 'Portal Active' : 'Portal Offline'}
                                    </span>
                                </div>

                                {isPortalActive && portalUrl && (
                                    <a
                                        href={portalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-indigo-600 hover:text-indigo-800 hover:underline"
                                    >
                                        Open Hub <Link size={10} />
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-6">
                            {/* 1. Guest Card */}
                            <div className="bg-white rounded-[24px] p-1 shadow-xl shadow-indigo-100/50 border border-indigo-50 group hover:-translate-y-1 transition-transform duration-300">
                                <div className="p-4 flex flex-col items-center text-center">
                                    <div className="w-20 h-20 rounded-2xl bg-slate-100 mb-3 overflow-hidden shadow-inner relative">
                                        {episode?.headshotUrl ? (
                                            <img src={episode.headshotUrl} alt="Guest" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <User size={32} />
                                            </div>
                                        )}
                                    </div>
                                    <h3 className="font-bold text-slate-900 font-['Outfit'] text-lg leading-tight">
                                        {episode?.founderName || 'Guest Name'}
                                    </h3>
                                    <p className="text-xs text-slate-500 font-medium mt-1">
                                        {episode?.companyName || 'Company'}
                                    </p>
                                </div>

                                {/* Contact / Socials Mock */}
                                <div className="bg-slate-50 rounded-[20px] p-3 flex justify-between items-center text-xs font-medium text-slate-500">
                                    <span className="flex items-center gap-1"><Link size={10} /> LinkedIn</span>
                                    <span className="flex items-center gap-1"><Link size={10} /> Website</span>
                                </div>
                            </div>

                            {/* 2. Prep Checklist */}
                            <PrepChecklist episodeId={episode?.id} />

                            {research.length === 0 ? (
                                // Empty State research
                                <div className="text-center p-6 border-2 border-dashed border-slate-100 rounded-[24px] bg-slate-50/50">
                                    <Sparkles size={24} className="mx-auto text-indigo-300 mb-2" />
                                    <p className="text-xs font-bold text-slate-500">No research yet</p>
                                    <button className="mt-3 px-4 py-2 bg-white border border-indigo-100 text-indigo-600 text-[10px] font-bold uppercase rounded-xl hover:shadow-sm transition-all shadow-indigo-100">
                                        Run Deep Search
                                    </button>
                                </div>
                            ) : (
                                // Research Drops
                                <div className="space-y-3">
                                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-2">Research Drops</h3>
                                    {research.map((item, idx) => (
                                        <div
                                            key={idx}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, item)}
                                            className="bg-white border border-slate-100 p-3 rounded-xl shadow-sm hover:shadow-md hover:border-indigo-100 transition-all cursor-grab active:cursor-grabbing group relative"
                                        >
                                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button className="p-1 hover:bg-slate-100 rounded-md text-slate-400"><Copy size={12} /></button>
                                            </div>
                                            <h4 className="text-xs font-bold text-slate-800 mb-1 leading-snug pr-4">{item.title}</h4>
                                            <p className="text-[10px] text-slate-500 line-clamp-3 leading-relaxed">
                                                {item.content}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ContextSidebar;
